import { IconCornerDownRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, TextField, TextFieldType } from '@cfa/react-components';

import {
  IntlSubcategoryDTO,
  IntlSubtitleDTO,
} from '@/services/content-api-types';
import { LanguageObject } from '@/types';

import Constants from 'constants/index';

import AddResourceToSubtitleButton from '../AddResourceToSubtitleButton';
import SubtitleDocuments from '../SubtitleDocuments';

import { DocumentDto } from '@/_generated/api-clients/xp';
import { setAdminState, useFocusAdminState } from '@/routes/Admin/state';

interface EditSectionProps {
  addSectionFormIsValid: boolean;
  countryCode: string;
  handleAddSectionCancel: () => void;
  index: number;
  onSaveSubtitle: (
    pendingSectionMutation: { name: LanguageObject },
    index: number,
  ) => void;
  openInNewTab: boolean;
  pendingSectionMutation: {
    name: { en: string; es: string };
  };
  setPendingSectionMutation: (section: {
    name: { en: string; es: string };
  }) => void;
  sectionEditType: 'add' | 'edit' | undefined;
  setSectionEditType: (type: 'add' | 'edit' | undefined) => void;
}

const EditSection: React.FC<EditSectionProps> = ({
  addSectionFormIsValid,
  countryCode,
  handleAddSectionCancel,
  index,
  onSaveSubtitle,
  openInNewTab,
  pendingSectionMutation,
  setPendingSectionMutation,
  sectionEditType,
  setSectionEditType,
}) => {
  const { t } = useTranslation();

  const { selectedSubtitle, setSelectedSubtitle } =
    useFocusAdminState('selectedSubtitle');

  const { selectedSubtitleIndex, setSelectedSubtitleIndex } =
    useFocusAdminState('selectedSubtitleIndex');

  const { temporaryCategory, setTemporaryCategory } =
    useFocusAdminState('temporaryCategory');

  const showDocuments = (selectedSubtitle?.documents.length ?? 0) > 0;

  const handleEditSection = ({
    index,
    language,
    name,
  }: {
    index: number;
    language: string;
    name: string | LanguageObject;
  }) => {
    setTemporaryCategory({
      ...temporaryCategory,
      subcategories: temporaryCategory.subcategories
        ? temporaryCategory.subcategories.map((it, ind) =>
            ind === index
              ? ({
                  ...it,
                  subtitles: it.subtitles?.map(
                    (section: { name: LanguageObject }, idx: number) => {
                      if (idx === selectedSubtitleIndex) {
                        return {
                          ...section,
                          name:
                            language === Constants.LANGUAGE_OPTIONS.ENGLISH
                              ? { en: name, es: section.name.es }
                              : { en: section.name.en, es: name },
                        };
                      } else {
                        return section;
                      }
                    },
                  ),
                } as IntlSubcategoryDTO)
              : it,
          )
        : [],
    });
  };

  const onPendingSectionNameChange = (
    event: React.ChangeEvent<TextFieldType>,
  ) => {
    const name = event.target.value;
    if (name.length) {
      setPendingSectionMutation({
        ...pendingSectionMutation,
        name: { en: name, es: pendingSectionMutation.name.es },
      });
    } else {
      setPendingSectionMutation({
        ...pendingSectionMutation,
        name: { en: '', es: pendingSectionMutation.name.es },
      });
    }
  };

  const onPendingSectionTranslationChange = (
    event: React.ChangeEvent<TextFieldType>,
  ) => {
    const translation = event.target.value;
    if (translation.length) {
      setPendingSectionMutation({
        ...pendingSectionMutation,
        name: { en: pendingSectionMutation.name.en, es: translation },
      });
    } else {
      setPendingSectionMutation({
        ...pendingSectionMutation,
        name: { en: pendingSectionMutation.name.en, es: '' },
      });
    }
  };

  const deleteDocument = (
    subcategoryIndex: number,
    subtitleIndex: number,
    document: DocumentDto,
    documentIsMarkedAsVisible: boolean,
  ) => {
    setAdminState('selectedSubcategoryIndex', subcategoryIndex);
    setSelectedSubtitleIndex(subtitleIndex);
    setAdminState('selectedDocument', document);
    setAdminState('documentIsVisibleInSubtitle', documentIsMarkedAsVisible);
    setAdminState('confirmationModalType', 'documentDelete');
  };

  const handleToggleDocument = (
    subcategoryIndex: number,
    subtitleIndex: number,
    document: DocumentDto,
    documentIsMarkedAsVisible: boolean,
  ) => {
    setAdminState('selectedSubcategoryIndex', subcategoryIndex);
    setSelectedSubtitleIndex(subtitleIndex);
    setAdminState('selectedDocument', document);
    setAdminState('documentIsVisibleInSubtitle', documentIsMarkedAsVisible);
    setAdminState('confirmationModalType', 'documentVisibility');
  };

  const handleAddResourceToExistingSubtitle = (
    subcategoryIndex: number,
    subtitleIndex: number,
  ) => {
    setAdminState('selectedSubcategoryIndex', subcategoryIndex);
    setSelectedSubtitleIndex(subtitleIndex);
    setAdminState('showAddProcedurePopUp', true);
  };

  const translationIsRequired = [
    Constants.ADMIN_MANAGING_COUNTRIES.CA.LABEL_KEY,
    Constants.ADMIN_MANAGING_COUNTRIES.GB.LABEL_KEY,
    Constants.ADMIN_MANAGING_COUNTRIES.SG.LABEL_KEY,
  ].includes(countryCode);

  return (
    <>
      <div className="flex w-full flex-row pb-2">
        <TextField
          data-testid={'EditSectionNameInput'}
          fullWidth
          label={t('Admin.sectionName')}
          onChange={
            sectionEditType === 'edit'
              ? event => {
                  setSelectedSubtitle({
                    ...selectedSubtitle,
                    name: {
                      en: event.target.value,
                      es: selectedSubtitle?.name.es ?? '',
                    },
                  } as IntlSubtitleDTO);
                  handleEditSection({
                    index,
                    language: Constants.LANGUAGE_OPTIONS.ENGLISH,
                    name: event.target.value,
                  });
                }
              : onPendingSectionNameChange
          }
          placeholder={t('Admin.addSectionNamePlaceholder')}
          required={true}
          value={
            sectionEditType === 'edit'
              ? selectedSubtitle?.name.en
              : pendingSectionMutation.name.en
          }
        />
      </div>
      <div className="flex w-full flex-row pb-2">
        <IconCornerDownRight size={35} />
        <TextField
          data-testid={'EditSectionNameTranslationInput'}
          fullWidth
          label={t('Admin.spanishTranslation')}
          onChange={
            sectionEditType === 'edit'
              ? event => {
                  handleEditSection({
                    index,
                    language: Constants.LANGUAGE_OPTIONS.SPANISH,
                    name: event.target.value,
                  });
                  setSelectedSubtitle({
                    ...selectedSubtitle,
                    name: {
                      en: selectedSubtitle?.name.en,
                      es: event.target.value,
                    },
                  } as IntlSubtitleDTO);
                }
              : onPendingSectionTranslationChange
          }
          placeholder={t('Admin.addSectionTranslationPlaceholder')}
          required={!translationIsRequired}
          value={
            sectionEditType === 'edit'
              ? selectedSubtitle?.name.es
              : pendingSectionMutation.name.es
          }
        />
      </div>
      <>
        {showDocuments && selectedSubtitle && (
          <SubtitleDocuments
            disableSixDotMenuHandles={true}
            handleDocumentDelete={(document, documentIsMarkedAsVisible) =>
              deleteDocument(
                index,
                selectedSubtitleIndex,
                document,
                documentIsMarkedAsVisible,
              )
            }
            handleToggleDocument={(document, documentIsMarkedAsVisible) =>
              handleToggleDocument(
                index,
                selectedSubtitleIndex,
                document,
                documentIsMarkedAsVisible,
              )
            }
            openDocumentInNewTab={openInNewTab}
            subtitle={selectedSubtitle}
          />
        )}
        {!sectionEditType && (
          <AddResourceToSubtitleButton
            onAdminAddResourceToSubtitle={() =>
              handleAddResourceToExistingSubtitle(index, index)
            }
          />
        )}
      </>
      {sectionEditType === 'add' && (
        <div className="flex w-full flex-row pb-2">
          <div className="mt-4 mb-8 flex justify-center gap-4">
            <Button
              color="secondary"
              onClick={handleAddSectionCancel}
              variant="outlined"
            >
              {t('Button.cancel')}
            </Button>
            <Button
              color="secondary"
              data-testid="AddSectionSaveButton"
              disabled={!addSectionFormIsValid}
              onClick={() => {
                setSectionEditType(undefined);
                onSaveSubtitle(pendingSectionMutation, index);
              }}
              variant="filled"
            >
              {t('Button.add')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default EditSection;
